import "@/App.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ArrowRight, IndianRupee, MapPin, Users, Sparkles, ShieldCheck,
  TrendingUp, Headphones, BadgeCheck, CheckCircle2, Phone, Mail, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "", ...props }) => (
  <section {...props} className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);


const Card = ({ children, className = "", ...props }) => (
  <div {...props} className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const GoldButton = ({ children, className = "", type = "button", ...props }) => (
  <button
    type={type}
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Logo = () => (
  <a
    href="/"
    className="flex items-center"
    data-testid="cinevibe-logo"
  >
    <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
      <img
        src="/images/logo.png"
        alt="CineVibe logo"
        className="w-full h-full object-cover"
      />
    </div>
  </a>
);
const scrollToEnquire = () => {
  const el = document.getElementById("enquire");
  if (!el) return;
  // account for sticky header height (~80px) so the form heading isn't hidden
  const headerOffset = 96;
  const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
  window.scrollTo({ top, behavior: "smooth" });
};

const Header = () => (
  <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
    <Section className="flex items-center justify-between py-4">
      <Logo />
      <div className="flex items-center gap-3">
        <a href="/" data-testid="back-home" className="text-[12px] tracking-[0.16em] text-[#d8cdb3] hover:text-[#f4d089]">← BACK</a>
        <GoldButton
  data-testid="header-enquire"
  className="text-sm tracking-[0.15em]"
  onClick={scrollToEnquire}
>
  ENQUIRE NOW
</GoldButton>
      </div>
    </Section>
  </header>
);

const Stat = ({ value, label }) => (
  <Card className="p-5 text-center">
    <div className="text-3xl font-display text-[#f4d089]">{value}</div>
    <div className="text-[11px] tracking-[0.18em] uppercase text-[#a89878] mt-2">{label}</div>
  </Card>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <Card className="p-6">
    <div className="w-10 h-10 rounded-md bg-[#1a0f06] border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089]">
      <Icon size={18} />
    </div>
    <h4 className="text-[#f4d089] font-display text-lg mt-3">{title}</h4>
    <p className="text-sm text-[#bfb393] mt-2 leading-relaxed">{desc}</p>
  </Card>
);

const investments = ["₹25-50 L", "₹50 L - 1 Cr", "₹1 - 2 Cr", "₹2 Cr+"];
const spaces = ["< 1000 sq ft", "1000-2000 sq ft", "2000-3500 sq ft", "3500+ sq ft"];
const timelines = ["Immediate", "1-3 months", "3-6 months", "Just exploring"];

function EnquireForm() {
  const [f, setF] = useState({
    name: "", phone: "", email: "", city: "",
    investment: "", space_available: "", timeline: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!f.name.trim()) return setErr("Please enter your name");
    if (f.phone.replace(/\D/g, "").length < 10) return setErr("Please enter a valid 10-digit phone");
    setBusy(true);
    try {
      await axios.post(`${API}/franchise/leads`, f);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card className="p-10 text-center" data-testid="enquire-success">
        <CheckCircle2 className="mx-auto text-[#7ad99c]" size={48} />
        <h3 className="font-display text-2xl text-[#f4d089] mt-4">Thank you, {f.name.split(" ")[0]}!</h3>
        <p className="text-[#bfb393] mt-2 max-w-md mx-auto">
          Your franchise enquiry is in. Our team will reach out at <span className="text-[#f4d089]">+91 {f.phone.replace(/\D/g, "").slice(-10)}</span> within 24 hours.
        </p>
        <button
          data-testid="enquire-another"
          onClick={() => { setDone(false); setF({ name: "", phone: "", email: "", city: "", investment: "", space_available: "", timeline: "", message: "" }); }}
          className="mt-6 text-xs tracking-widest text-[#f4d089] underline"
        >
          SUBMIT ANOTHER ENQUIRY
        </button>
      </Card>
    );
  }

  const inputCls = "w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574]";
  const labelCls = "text-[11px] tracking-[0.18em] uppercase text-[#a89878] mb-1.5 block";

  return (
    <Card className="p-6 sm:p-8 scroll-mt-28" id="enquire">
      <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">FRANCHISE ENQUIRY</div>
      <h3 className="font-display text-2xl sm:text-3xl text-white mt-1">Tell us about you</h3>
      <p className="text-[#bfb393] text-sm mt-1">We'll get back within 24 hours with a detailed franchise pack.</p>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input data-testid="enq-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input data-testid="enq-phone" className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit mobile" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="enq-email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" />
        </div>
        <div>
          <label className={labelCls}>Preferred City</label>
          <input data-testid="enq-city" className={inputCls} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="e.g. Pune" />
        </div>
        <div>
          <label className={labelCls}>Investment Capacity</label>
          <select data-testid="enq-investment" className={inputCls} value={f.investment} onChange={(e) => setF({ ...f, investment: e.target.value })}>
            <option value="">Select range</option>
            {investments.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Space Available</label>
          <select data-testid="enq-space" className={inputCls} value={f.space_available} onChange={(e) => setF({ ...f, space_available: e.target.value })}>
            <option value="">Select size</option>
            {spaces.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Timeline to Start</label>
          <div className="flex flex-wrap gap-2">
            {timelines.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`enq-timeline-${t.toLowerCase().replace(/\s|-/g, "_")}`}
                onClick={() => setF({ ...f, timeline: t })}
                className={`text-xs px-3 py-2 rounded-md border transition ${f.timeline === t ? "border-[#d4a574] bg-[#1a0f06] text-[#f4d089]" : "border-[#3a2a14] text-[#bfb393] hover:border-[#d4a574]/60"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Anything you'd like to share?</label>
          <textarea data-testid="enq-message" rows={3} className={inputCls} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Background, business experience, goals…" />
        </div>
      </div>

      {err && <div data-testid="enq-error" className="text-red-400 text-sm mt-4">{err}</div>}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <GoldButton data-testid="enq-submit" onClick={submit} disabled={busy} className="text-sm tracking-[0.15em] inline-flex items-center gap-2 justify-center">
          {busy ? "Submitting…" : "SUBMIT ENQUIRY"} <ArrowRight size={16} />
        </GoldButton>
        <span className="text-[11px] text-[#a89878]">By submitting, you agree to be contacted by The CineVibe franchise team.</span>
      </div>
    </Card>
  );
}

export default function Franchise() {
  const [c, setC] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/franchise`)
      .then((r) => setC(r.data))
      .catch(() => setC({}));     // fallback → defaults still render
  }, []);

  // safe getter: returns saved value if present, else fallback string
  const t = (k, fallback = "") => (c && c[k] != null ? c[k] : fallback);

  // wait for first fetch so we don't flash defaults
  if (!c) return <div className="bg-[#0a0604] min-h-screen" />;

  return (
    <div className="bg-[#0a0604] min-h-screen text-white">
      <Header />
      {/* Hero */}
      <Section className="pt-12 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] text-[#d4a574] border border-[#d4a574]/30 rounded-full px-3 py-1">
  <Sparkles size={12} /> {t("hero_eyebrow", "FRANCHISE OPPORTUNITY")}
</div>
<h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5">
  {t("hero_title_pre", "Own a piece of")}{" "}
  <span className="text-[#f4d089]">{t("hero_title_highlight", "India's most loved")}</span>{" "}
  {t("hero_title_post", "private cinema brand.")}
</h1>
<p className="text-[#bfb393] mt-5 max-w-xl leading-relaxed">
  {t("hero_subtitle", "The CineVibe is reinventing entertainment — premium private screenings, in-room dining and unforgettable celebrations. Partner with us to launch in your city.")}
</p>
<div className="flex flex-wrap gap-3 mt-7">
  <GoldButton
  data-testid="hero-enquire"
  className="text-sm tracking-[0.15em]"
  onClick={scrollToEnquire}
>
  {t("hero_cta_primary", "START YOUR ENQUIRY")}
</GoldButton>
  <button
    type="button"
    onClick={() => {
      const el = document.getElementById("why");
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.pageYOffset - 96;
      window.scrollTo({ top, behavior: "smooth" });
    }}
    className="text-xs tracking-[0.18em] text-[#f4d089] border border-[#d4a574]/40 rounded-md px-5 py-3 inline-flex items-center hover:bg-[#1a0f06]"
  >
    {t("hero_cta_secondary", "WHY THE CINEVIBE")}
  </button>
</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
  <Stat value={t("stat1_value", "₹5-10 L")}   label={t("stat1_label", "Monthly Potential*")} />
  <Stat value={t("stat2_value", "18-24 mo")}  label={t("stat2_label", "Payback Period*")} />
  <Stat value={t("stat3_value", "3+ cities")} label={t("stat3_label", "Live Branches")} />
  <Stat value={t("stat4_value", "1,200+")}    label={t("stat4_label", "Happy Guests")} />
</div>
        </div>
      </Section>

      {/* Why */}
      <Section className="py-14" id="why">
        <div className="text-center max-w-2xl mx-auto">
  <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{t("why_eyebrow", "WHY PARTNER WITH US")}</div>
  <h2 className="font-display text-3xl sm:text-4xl mt-2">{t("why_heading", "A turnkey, asset-light, premium model")}</h2>
  <p className="text-[#bfb393] text-sm mt-3">{t("why_subtitle", "From location scouting to launch marketing — we do the heavy lifting.")}</p>
</div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
  {(c.why_items && c.why_items.length ? c.why_items : [
    { title: "Strong Unit Economics", desc: "Compact footprint, high revenue per sq ft, multiple monetisation streams (screenings, F&B, celebrations)." },
    { title: "Proven Playbook",       desc: "SOPs, vendor network and tech stack ready on day one. We've cracked the model so you don't have to." },
    { title: "End-to-End Support",    desc: "Site selection, build-out guidance, training, central marketing and ongoing operational support." },
    { title: "Differentiated Brand",  desc: "A loved D2C brand with a recognisable identity, premium positioning and an active community." },
    { title: "Central Booking Engine",desc: "Get bookings from our website, app and partner channels — automatically routed to your branch." },
    { title: "Quality First",         desc: "Tight quality controls keep your brand promise — and your guests coming back." },
  ]).map((it, i) => {
    const icons = [TrendingUp, ShieldCheck, Headphones, Sparkles, Users, BadgeCheck];
    const Icon = icons[i] || Sparkles;
    return <Feature key={i} icon={Icon} title={it.title} desc={it.desc} />;
  })}
</div>
      </Section>

      {/* How it works */}
      <Section className="py-14">
  <div className="text-center max-w-2xl mx-auto">
    <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{t("how_eyebrow", "HOW IT WORKS")}</div>
    <h2 className="font-display text-3xl sm:text-4xl mt-2">{t("how_heading", "From enquiry to opening")}</h2>
  </div>
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
    {(c.how_steps && c.how_steps.length ? c.how_steps : [
      { title: "1. Enquiry",      desc: "Submit the form. Our team calls within 24 hours." },
      { title: "2. Discovery",    desc: "Share goals, location, capital. Receive franchise pack & FOFO terms." },
      { title: "3. Sign & Build", desc: "Sign agreement. We help you scout, design and build out the venue." },
      { title: "4. Launch",       desc: "Staff training, soft launch and a coordinated grand opening campaign." },
    ]).map((s, i) => {
      const icons = [Phone, Mail, Building2, Sparkles];
      const Icon = icons[i] || Phone;
      return (
        <Card key={i} className="p-5">
          <div className="w-10 h-10 rounded-md bg-[#1a0f06] border border-[#d4a574]/30 flex items-center justify-center text-[#f4d089]"><Icon size={18} /></div>
          <div className="font-display text-lg text-[#f4d089] mt-3">{s.title}</div>
          <div className="text-sm text-[#bfb393] mt-2 leading-relaxed">{s.desc}</div>
        </Card>
      );
    })}
  </div>
</Section>

      {/* Numbers / Investment */}
      <Section className="py-14">
  <Card className="p-6 sm:p-10">
    <div className="grid md:grid-cols-3 gap-6">
      <div>
        <IndianRupee className="text-[#f4d089]" size={24} />
        <div className="text-[#a89878] text-xs tracking-widest mt-3">{t("inv_amount_label", "INVESTMENT*")}</div>
        <div className="font-display text-2xl text-white mt-1">{t("inv_amount_value", "₹50L – ₹2 Cr")}</div>
        <p className="text-[#bfb393] text-sm mt-2">{t("inv_amount_note", "All-inclusive: build-out, equipment, tech, brand fee.")}</p>
      </div>
      <div>
        <MapPin className="text-[#f4d089]" size={24} />
        <div className="text-[#a89878] text-xs tracking-widest mt-3">{t("inv_space_label", "SPACE*")}</div>
        <div className="font-display text-2xl text-white mt-1">{t("inv_space_value", "1500 – 3500 sq ft")}</div>
        <p className="text-[#bfb393] text-sm mt-2">{t("inv_space_note", "High-street or premium mall locations preferred.")}</p>
      </div>
      <div>
        <TrendingUp className="text-[#f4d089]" size={24} />
        <div className="text-[#a89878] text-xs tracking-widest mt-3">{t("inv_roi_label", "TARGET ROI*")}</div>
        <div className="font-display text-2xl text-white mt-1">{t("inv_roi_value", "18–24 months")}</div>
        <p className="text-[#bfb393] text-sm mt-2">{t("inv_roi_note", "Subject to location, occupancy and operations.")}</p>
      </div>
    </div>
    <div className="text-[10px] text-[#7a6c54] mt-6">{t("inv_footer_note", "*Indicative ranges. Actual numbers shared during discovery.")}</div>
  </Card>
</Section>

      {/* Form */}
      <Section className="py-14">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
  <div className="text-[11px] tracking-[0.2em] text-[#d4a574]">{t("form_eyebrow", "GET IN TOUCH")}</div>
  <h2 className="font-display text-3xl sm:text-4xl mt-2">{t("form_heading", "Let's build the next The CineVibe — together.")}</h2>
  <p className="text-[#bfb393] mt-4 leading-relaxed">
    {t("form_subtitle", "Drop your details and our franchise team will share a complete deck with unit economics, agreement details and the next steps.")}
  </p>
  <div className="mt-6 space-y-3">
    <div className="flex items-center gap-3 text-sm text-[#d8cdb3]"><Phone size={16} className="text-[#f4d089]" /> {t("contact_phone", "+91 90000 00000")}</div>
    <div className="flex items-center gap-3 text-sm text-[#d8cdb3]"><Mail size={16} className="text-[#f4d089]" /> {t("contact_email", "franchise@cinevibe.in")}</div>
  </div>
</div>
          <div className="lg:col-span-3">
            <EnquireForm />
          </div>
        </div>
      </Section>

      <footer className="border-t border-[#d4a574]/15 mt-10">
        <Section className="py-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-[#a89878]">
          <div className="leading-relaxed">
            © {new Date().getFullYear()} The CineVibe — Private Screens & Café<br />
            BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
          </div>
          <a href="/" className="hover:text-[#f4d089]">Back to Home</a>
        </Section>
      </footer>
    </div>
  );
}
